"use strict";
/**
 * emit.js implements an Emitter class which emits events to a list of listeners. Listeners are
 * simply functions to call, and "emitting an event" just calls those functions.
 *
 * This is similar to Backbone events, with more focus on efficiency. Both inserting and removing
 * listeners is constant time.
 *
 * To create an emitter:
 *    let emitter = new Emitter();
 *
 * To add a listener:
 *    let listener = fooEmitter.addListener(callback);
 * To remove a listener:
 *    listener.dispose();
 *
 * The only way to remove a listener is to dispose the Listener object returned by addListener().
 * You can often use autoDispose to do this automatically when subscribing in a constructor:
 *    this.autoDispose(fooEmitter.addListener(this.onFoo, this));
 *
 * To emit an event, call emit() with any number of arguments:
 *    emitter.emit("hello", "world");
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Listener = exports.Emitter = exports.LLink = void 0;
// Note about a possible alternative implementation.
//
// We could implement the same interface using an array of listeners. Certain issues apply, in
// particular with removing listeners from inside emit(), and in ensuring that removals are
// constant time on average. Such an implementation was attempted and timed. The result is that
// compared to the linked-list implementation here, add/remove combination could be made nearly
// twice faster (on average), while emit and add/remove/emit are consistently slightly slower.
//
// The implementation here was chosen based on those timings, and as the simpler one. For example,
// on one setup (macbook, node4, 5-listener queue), add+remove take 0.1us, while add+remove+emit
// take 3.82us. (In array-based implementation with same set up, add+remove is 0.06us, while
// add+remove+emit is 4.80us.)
// The private property name to hold next/prev pointers.
function _noop() { }
/**
 * This is an implementation of a doubly-linked list, with just the minimal functionality we need.
 */
class LLink {
    constructor() {
        this._next = null;
        this._prev = null;
        // This immediate circular reference might be undesirable for GC, but might not matter, and
        // makes the linked list implementation simpler and faster.
        this._next = this;
        this._prev = this;
    }
    isDisposed() {
        return !this._next;
    }
    _insertBefore(next, node) {
        const last = next._prev;
        last._next = node;
        next._prev = node;
        node._prev = last;
        node._next = next;
    }
    _removeNode(node) {
        if (node._prev) {
            node._prev._next = node._next;
            node._next._prev = node._prev;
        }
        node._prev = node._next = null;
    }
    _disposeList() {
        let node = this;
        let next = node._next;
        while (next !== null) {
            node._next = node._prev = null;
            node = next;
            next = node._next;
        }
    }
}
exports.LLink = LLink;
class Emitter extends LLink {
    /**
     * Constructs an Emitter object.
     */
    constructor() {
        super();
        this._changeCB = _noop;
        this._changeCBContext = undefined;
    }
    /**
     * Adds a listening callback to the list of functions to call on emit().
     * @param {Function} callback: Function to call.
     * @param {Object} optContext: Context for the function.
     * @returns {Listener} Listener object. Its dispose() method removes the callback from the list.
     */
    addListener(callback, optContext) {
        return new Listener(this, callback, optContext);
    }
    /**
     * Calls all listener callbacks, passing all arguments to each of them.
     */
    emit(...args) {
        Listener.callAll(this._next, this, args);
    }
    /**
     * Sets the single callback that would get called when a listener is added or removed.
     * @param {Function} changeCB(hasListeners): Function to call after a listener is added or
     *    removed. It's called with a boolean indicating whether this Emitter has any listeners.
     *    Pass in `null` to unset the callback. Note that it can be called multiple times in a row
     *    with hasListeners `true`.
     */
    setChangeCB(changeCB, optContext) {
        this._changeCB = changeCB || _noop;
        this._changeCBContext = optContext;
    }
    /**
     * Helper used by Listener class, but not intended for public usage.
     */
    _triggerChangeCB() {
        this._changeCB.call(this._changeCBContext, this.hasListeners());
    }
    /**
     * Returns whether this Emitter has any listeners.
     */
    hasListeners() {
        return this._next !== this;
    }
    /**
     * Disposes the Emitter. It breaks references between the emitter and all the items, allowing
     * for better garbage collection. It effectively disposes all current listeners.
     */
    dispose() {
        this._disposeList();
        this._changeCB = _noop;
        this._changeCBContext = undefined;
    }
}
exports.Emitter = Emitter;
/**
 * Listener object wraps a callback added to an Emitter, allowing for O(1) removal when the
 * listener is disposed.
 */
class Listener extends LLink {
    constructor(emitter, callback, context) {
        super();
        this.emitter = emitter;
        this.callback = callback;
        this.context = context;
        this._insertBefore(emitter, this);
        emitter._triggerChangeCB();
    }
    static callAll(begin, end, args) {
        while (begin !== end) {
            const lis = begin;
            lis.callback.call(lis.context, ...args);
            begin = lis._next;
        }
    }
    dispose() {
        if (this.isDisposed()) {
            return;
        }
        this._removeNode(this);
        this.emitter._triggerChangeCB();
    }
}
exports.Listener = Listener;
//# sourceMappingURL=emit.js.map